import type { QueryClient } from "@tanstack/react-query"
import { preloadChatView } from "@/lib/chat-chunk"
import { sessionQuery } from "@/lib/queries"
import { projectsQuery } from "@/lib/workspace-api"
import { agentChatName, prefetchChat } from "./chat-cache"
import { projectOfPath, WARM_HOLD_MS, warmProject } from "./connection"

/**
 * Project load speed (2026-10-17, ux-pass-3 P1/P2). Opening a project used to be a chain: session → workspace list →
 * render → project socket → snapshot → chat chunk → chat transcript. Everything that does not depend on the step
 * before now starts together, as early as the URL is known:
 * - the project's socket (its snapshot streams in while the rest of the page loads),
 * - the project list (the page's "does this project exist" check),
 * - on the Chat tab: the chat's code chunk and the agent's stored transcript.
 * Never blocks and never throws: each piece is also loaded by the page itself if this did not run or failed.
 */
export interface WarmProjectPage {
  readonly queryClient: QueryClient
  readonly workspaceId: string
  readonly projectId: string
  readonly userId: string
  /** The `view` search param (`chat` preloads the chat). */
  readonly view?: string | undefined
  /** The `agent` search param on the Chat tab (an agent id or handle; absent = the lead). */
  readonly agent?: string | undefined
  readonly holdMs?: number
  /** Injected in tests. */
  readonly deps?: {
    readonly warm?: typeof warmProject
    readonly chatChunk?: () => void
    readonly prefetch?: (name: string) => void
  }
}

const AGENT_ID = /^agt_[A-Za-z0-9]+$/

export const warmProjectPage = (input: WarmProjectPage): void => {
  const { queryClient, workspaceId, projectId, userId, view, agent, deps = {} } = input
  try {
    ;(deps.warm ?? warmProject)(workspaceId, projectId, userId, input.holdMs ?? WARM_HOLD_MS)
    const projects = queryClient.ensureQueryData(projectsQuery(workspaceId))
    projects.catch(() => {})
    if (view !== "chat") return
    ;(deps.chatChunk ?? preloadChatView)()
    const prefetch = deps.prefetch ?? ((name: string) => prefetchChat(name))
    // a handle needs the roster (the snapshot) to resolve: that chat loads its transcript itself
    if (agent !== undefined) {
      if (AGENT_ID.test(agent)) prefetch(agentChatName(workspaceId, agent))
      return
    }
    projects
      .then((list) => {
        const lead = list.find((p) => p.id === projectId)?.leadAgentId
        if (lead) prefetch(agentChatName(workspaceId, lead))
      })
      .catch(() => {})
  } catch {
    // warming is best effort
  }
}

/**
 * At startup, for a project URL: warm the page as soon as the session is known (before the route guards have the
 * workspace list and the page has rendered). Signed out or another page: nothing.
 */
export const startProjectBoot = (
  queryClient: QueryClient,
  location: { pathname: string; search: string },
): void => {
  const target = projectOfPath(location.pathname)
  if (!target) return
  const search = new URLSearchParams(location.search)
  queryClient
    .ensureQueryData(sessionQuery)
    .then((session) => {
      if (!session) return
      warmProjectPage({
        queryClient,
        ...target,
        userId: session.user.id,
        view: search.get("view") ?? undefined,
        agent: search.get("agent") ?? undefined,
      })
    })
    .catch(() => {})
}
